/**
 * TechStackSection - Pipeline/flow visualization
 * Shows how a message flows through NARA's technology stack
 */
import React from 'react';
import { Typography, Box, Container } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudIcon from '@mui/icons-material/Cloud';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import { COLORS, STYLES, TYPOGRAPHY, SPACING, TRANSITIONS } from './landingTheme';
import { ScrollReveal } from './animations';

// Pipeline steps — the journey of a patient message
const PIPELINE_STEPS = [
  {
    step: '01',
    label: 'Recepción',
    tech: 'WhatsApp + Twilio',
    description: 'El paciente escribe por WhatsApp. Twilio entrega el mensaje a NARA en milisegundos.',
    icon: WhatsAppIcon,
    color: '#25D366',
  },
  {
    step: '02',
    label: 'Inteligencia',
    tech: 'OpenAI GPT-4o',
    description: 'Clasifica la intención del mensaje, diagnostica síntomas y gestiona citas con function calling.',
    icon: AutoAwesomeIcon,
    color: '#10a37f',
  },
  {
    step: '03',
    label: 'Memoria',
    tech: 'Google Embeddings',
    description: 'Busca el historial del paciente con memoria semántica. Recuerda alergias, diagnósticos y preferencias.',
    icon: CloudIcon,
    color: '#4285f4',
  },
  {
    step: '04',
    label: 'Visión',
    tech: 'Anthropic Claude',
    description: 'Analiza imágenes médicas, documentos, recetas y resultados de laboratorio con visión artificial.',
    icon: VisibilityIcon,
    color: '#d4a27f',
  },
];

// Single pipeline node
const PipelineNode = ({ item, isLast }) => {
  const IconComponent = item.icon;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'row', md: 'column' },
        alignItems: { xs: 'flex-start', md: 'center' },
        gap: { xs: 2, md: 0 },
        flex: 1,
        position: 'relative',
        textAlign: { xs: 'left', md: 'center' },
      }}
    >
      {/* Step number — mobile only, left side */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0,
        }}
      >
        {/* Icon badge */}
        <Box
          sx={{
            width: 52,
            height: 52,
            minWidth: 52,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${item.color}, ${item.color}cc)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 6px 20px ${item.color}30`,
          }}
        >
          <IconComponent sx={{ fontSize: 26, color: '#fff' }} />
        </Box>
        {/* Vertical connector line — mobile */}
        {!isLast && (
          <Box
            sx={{
              width: 2,
              height: 40,
              background: `linear-gradient(180deg, ${item.color}40, ${COLORS.gray200})`,
              mt: 1,
            }}
          />
        )}
      </Box>

      {/* Desktop: icon badge centered */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          alignItems: 'center',
          mb: 2,
        }}
      >
        {/* Step number */}
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 700,
            color: item.color,
            letterSpacing: '0.08em',
            mb: 1.5,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          PASO {item.step}
        </Typography>

        {/* Icon badge */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${item.color}, ${item.color}cc)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 8px 28px ${item.color}25`,
            transition: TRANSITIONS.normal,
            '&:hover': {
              transform: 'scale(1.08)',
              boxShadow: `0 12px 36px ${item.color}35`,
            },
          }}
        >
          <IconComponent sx={{ fontSize: 32, color: '#fff' }} />
        </Box>
      </Box>

      {/* Text content */}
      <Box sx={{ flex: { xs: 1, md: 'none' }, minWidth: 0 }}>
        {/* Mobile step label */}
        <Typography
          sx={{
            display: { xs: 'block', md: 'none' },
            fontSize: '0.68rem',
            fontWeight: 700,
            color: item.color,
            letterSpacing: '0.06em',
            mb: 0.3,
          }}
        >
          PASO {item.step}
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: '1.05rem', md: '1.1rem' },
            fontWeight: 700,
            color: COLORS.textPrimary,
            mb: 0.3,
            lineHeight: 1.3,
          }}
        >
          {item.label}
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: '0.78rem', md: '0.8rem' },
            fontWeight: 600,
            color: item.color,
            mb: { xs: 0.5, md: 1 },
          }}
        >
          {item.tech}
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: '0.83rem', md: '0.85rem' },
            color: COLORS.textSecondary,
            lineHeight: 1.65,
            maxWidth: { md: 220 },
            mx: { md: 'auto' },
          }}
        >
          {item.description}
        </Typography>
      </Box>
    </Box>
  );
};

// Arrow connector between nodes — desktop only
const PipelineArrow = ({ fromColor, toColor }) => (
  <Box
    sx={{
      display: { xs: 'none', md: 'flex' },
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'center',
      pt: 3,
      px: 0.5,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
      }}
    >
      {/* Dashed line */}
      <Box
        sx={{
          width: { md: 28, lg: 40 },
          height: 2,
          background: `linear-gradient(90deg, ${fromColor}50, ${toColor}50)`,
          borderRadius: 1,
        }}
      />
      {/* Arrow head */}
      <ArrowForwardIcon
        sx={{
          fontSize: 16,
          color: `${toColor}70`,
          ml: -0.3,
        }}
      />
    </Box>
  </Box>
);

export default function TestimonialSection() {
  return (
    <Box sx={{ py: SPACING.sectionY, background: COLORS.gray50, position: 'relative', overflow: 'hidden' }}>
      {/* Decorative blobs */}
      <Box sx={{ position: 'absolute', top: '20%', right: '-6%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(16, 163, 127, 0.04) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', bottom: '10%', left: '-5%', width: 250, height: 250, borderRadius: '50%', background: 'radial-gradient(circle, rgba(66, 133, 244, 0.04) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <Container maxWidth="lg">
        {/* Section header */}
        <ScrollReveal>
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography
              sx={{
                color: COLORS.accent,
                fontSize: TYPOGRAPHY.tiny,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                mb: 1.5,
              }}
            >
              Tecnología
            </Typography>
            <Typography
              variant="h2"
              sx={{ ...STYLES.sectionTitle }}
            >
              El viaje de un mensaje
            </Typography>
            <Typography
              sx={{ ...STYLES.sectionSubtitle }}
            >
              Desde que tu paciente escribe hasta que recibe una respuesta inteligente — así trabaja NARA por dentro
            </Typography>
          </Box>
        </ScrollReveal>

        {/* Pipeline — horizontal on desktop, vertical on mobile */}
        <ScrollReveal delay={0.15}>
          {/* Desktop: horizontal pipeline with arrows */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'flex-start',
              justifyContent: 'center',
              px: { md: 2 },
            }}
          >
            {PIPELINE_STEPS.map((item, idx) => (
              <React.Fragment key={idx}>
                <PipelineNode item={item} isLast={idx === PIPELINE_STEPS.length - 1} />
                {idx < PIPELINE_STEPS.length - 1 && (
                  <PipelineArrow
                    fromColor={item.color}
                    toColor={PIPELINE_STEPS[idx + 1].color}
                  />
                )}
              </React.Fragment>
            ))}
          </Box>

          {/* Mobile: vertical pipeline */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              flexDirection: 'column',
              gap: 0,
              maxWidth: 420,
              mx: 'auto',
            }}
          >
            {PIPELINE_STEPS.map((item, idx) => (
              <PipelineNode key={idx} item={item} isLast={idx === PIPELINE_STEPS.length - 1} />
            ))}
          </Box>
        </ScrollReveal>

        {/* Response indicator */}
        <ScrollReveal delay={0.3}>
          <Box
            sx={{
              mt: { xs: 4, md: 5 },
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 3,
                py: 1.5,
                borderRadius: 3,
                background: COLORS.white,
                border: `1px solid ${COLORS.gray200}`,
                boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#25D366',
                  boxShadow: '0 0 8px rgba(37, 211, 102, 0.5)',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.6, transform: 'scale(1.3)' },
                  },
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: COLORS.textSecondary,
                }}
              >
                Respuesta enviada al paciente en {'<'}2 segundos
              </Typography>
            </Box>
          </Box>
        </ScrollReveal>
      </Container>
    </Box>
  );
}
